import { sidebarItemBase } from '@/layouts/full/vertical-sidebar/sidebarItem';

interface Permission {
    id: string;
    parent_id: string | null;
    entity: string;
    action: string | null;
    label: string;
    description: string;
    platform: string;
    created_at: string;
    updated_at: string | null;
    display_order: string | null;
    enabled: boolean;
    children: Permission[];
}

/**
 * Busca recursivamente un permiso por su action o entity en el array de permisos
 */
function hasPermission(permissions: Permission[], permissionKey: string): boolean {
    if (!permissions || !Array.isArray(permissions)) {
        return false;
    }

    return permissions.some((p: Permission) => {
        // Verificar si el permiso actual coincide por action o entity
        // Si action es null, usar entity como fallback
        const matchesByAction = p.action === permissionKey;
        const matchesByEntity = p.action === null && p.entity === permissionKey;
        const matches = (matchesByAction || matchesByEntity) && p.enabled;
        
        if (matches) {
            return true;
        }
        
        // Buscar recursivamente en children
        if (p.children && p.children.length > 0) {
            return hasPermission(p.children, permissionKey);
        }
        return false;
    });
}

/**
 * Verifica si el usuario tiene el permiso requerido desde localStorage
 */
function checkSystemPermission(requiresSystemPermission?: string | string[]): boolean {
    if (!requiresSystemPermission) {
        return true; // Si no requiere permiso, está habilitado
    }

    try {
        const permissionsUserStr = localStorage.getItem('permissionsUser');
        if (!permissionsUserStr) {
            return false; // Si no hay permisos en localStorage, no tiene acceso
        }

        const permissionsUser: Permission[] = JSON.parse(permissionsUserStr);
        
        // Si es un array de permisos, todos deben estar presentes
        if (Array.isArray(requiresSystemPermission)) {
            const result = requiresSystemPermission.every(permission => hasPermission(permissionsUser, permission));
            return result;
        }
        
        // Si es un solo permiso
        const result = hasPermission(permissionsUser, requiresSystemPermission);
        return result;
    } catch (error) {
        console.error('Error al verificar permisos:', error);
        return false; // En caso de error, no tiene acceso
    }
}

export function getEnableFirstRoute() {
    // Verificar si hay permisos en localStorage
    const permissionsUserStr = localStorage.getItem('permissionsUser');
    
    if (!permissionsUserStr) {
        console.warn('getEnableFirstRoute: No hay permisos en localStorage');
        return 'NoPermissions';
    }
    
    let permissionsUser: Permission[] = [];
    try {
        permissionsUser = JSON.parse(permissionsUserStr);
    } catch (error) {
        return 'NoPermissions';
    }
    
    // Filtrar items que no son headers y verificar permisos
    const enabledRoutes: any[] = [];
    
    for (const item of sidebarItemBase) {
        // Saltar headers
        if (item.header) {
            continue;
        }
        
        // Verificar si el item requiere permiso
        if (!item.requiresSystemPermission) {
            // Si no requiere permiso, está habilitado
            enabledRoutes.push({
                name: item.title,
                route: item.to?.name,
                requiresPermission: item.requiresSystemPermission
            });
            // Retornar el primero que no requiere permiso
            return item.to?.name || 'NoPermissions';
        }
        
        // Verificar permiso
        const hasPermission = checkSystemPermission(item.requiresSystemPermission);
        
        if (hasPermission) {
            enabledRoutes.push({
                name: item.title,
                route: item.to?.name,
                requiresPermission: item.requiresSystemPermission
            });
            // Retornar el primero que tiene permiso
            return item.to?.name || 'NoPermissions';
        }
    }
        
    return 'NoPermissions';
}